import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Resolve the tenant slug from (priority order):
 *  1) ?t=<slug> URL param
 *  2) First subdomain if the host has >=3 parts (e.g. ``acme.blitzz.fr``)
 *  3) ``localStorage.blitzz_brand_slug`` fallback.
 */
export function resolveBrandSlug() {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromParam = params.get("t");
    if (fromParam) return fromParam.toLowerCase();
    const host = window.location.hostname;
    const parts = host.split(".");
    const blacklist = new Set(["www", "localhost", "app", "preview", "emergentagent"]);
    if (parts.length >= 3 && !blacklist.has(parts[0])) {
      return parts[0].toLowerCase();
    }
    const stored = localStorage.getItem("blitzz_brand_slug");
    return stored || null;
  } catch (_) { return null; }
}

/** Apply brand colours to the document root as CSS variables. */
export function applyBranding(brand) {
  if (!brand) return;
  const root = document.documentElement;
  if (brand.brand_color_primary) root.style.setProperty("--brand-primary", brand.brand_color_primary);
  if (brand.brand_color_secondary) root.style.setProperty("--brand-secondary", brand.brand_color_secondary);
  if (brand.brand_favicon) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = brand.brand_favicon;
  }
  if (brand.brand_app_name) {
    document.title = brand.brand_app_name;
  }
}

/** React hook: resolves the tenant slug, fetches /public/branding/{slug},
 *  applies colours to the root, and exposes the branding payload.
 */
export function useBranding(slugOverride) {
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const slug = slugOverride || resolveBrandSlug();

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    let cancelled = false;
    axios.get(`${API}/public/branding/${slug}`)
      .then((r) => {
        if (cancelled) return;
        setBrand(r.data);
        applyBranding(r.data);
        // Cache the slug so subsequent pages keep the branding
        try { localStorage.setItem("blitzz_brand_slug", slug); } catch (_) {}
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [slug]);

  return { brand, loading, slug };
}
